/**
 * Donor Milk API Service
 *
 * Provides data-access methods for the Donor Milk tab.
 * Calls tms-api-inventoryManagement (/api/v1/milk-receivables) with
 * source_type = 'DONOR'.
 *
 * Field mapping between the UI view-model and the REST API:
 *   containerId   ↔  receipt_number
 *   donorName     ↔  provider_id  (UUID; name lookup TODO via patient service)
 *   volumeMl      ↔  volume_ml
 *   pumpedDate    ↔  received_at
 *   expiryDate    ↔  expiry_at
 *   status        ↔  milk_status  (backend enum: FRESH / FROZEN / THAWED)
 *   milkType      ↔  milk_type    (backend enum: PRETERM / TERM / FOREMILK / HINDMILK / SKIM_HUMAN_MILK / COLOSTRUM)
 *   location      ↔  location
   *   donorStatus   ↔  donor_status (backend enum: PENDING / CLEARED / APPROVED / REJECTED / EXPIRED; mirrors screening_status from Donor Approval flow)
 */
import type {
  DonorMilkContainer,
  DonorMilkQueryParams,
  MilkReceivableMilkStatus,
  MilkReceivableMilkType,
  MilkReceivableDonorStatus,
  DonorApprovalStatus,
  BulkReceiveFailure,
} from '../types';
import type { ApiMilkReceivable } from '../types';
import {
  listMilkReceivables,
  getMilkReceivableById,
  createMilkReceivable,
  createMilkReceivablesBulk,
  updateMilkReceivable,
  discardMilkReceivable,
} from './milk-receivables.api';
import { config } from '@/core/config';

// ── Response → view-model ─────────────────────────────────────────────────────

/** Maps backend donor_status (screening_status values) to UI display value */
const DONOR_STATUS_FROM_API: Record<MilkReceivableDonorStatus, DonorApprovalStatus> = {
  PENDING: 'Pending',
  CLEARED: 'Cleared',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  ON_HOLD: 'On Hold',
};

/** Maps UI donor status value to backend API screening_status value */
const DONOR_STATUS_TO_API: Record<DonorApprovalStatus, MilkReceivableDonorStatus> = {
  Pending: 'PENDING',
  Cleared: 'CLEARED',
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
  Expired: 'EXPIRED',
  'On Hold': 'ON_HOLD',
};

function toDonorMilkContainer(rec: ApiMilkReceivable): DonorMilkContainer {
  return {
    id: rec.receivable_id,
    containerId: rec.receipt_number,
    donorName: `${rec.provider_first_name} ${rec.provider_last_name}`.trim() || rec.provider_id,
    volumeMl: parseFloat(rec.volume_ml),
    remainingVolumeMl: parseFloat(rec.remaining_volume_ml),
    pumpedDate: rec.received_at,
    expiryDate: rec.expiry_at ?? null,
    milkType: rec.milk_type ?? 'TERM',
    status: rec.milk_status ?? 'FRESH',
    bottleStatus: rec.status,
    donorStatus: DONOR_STATUS_FROM_API[rec.donor_status] ?? 'pending',
    location: rec.location ?? '',
    createdAt: rec.created_at,
    updatedAt: rec.updated_at,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface CreateDonorMilkContainerPayload {
  containerId: string;
  /**
   * TODO: replace with a provider UUID lookup so the container is attributed to
   * the selected donor rather than the logged-in staff member.
   * Until the provider-search feature is implemented, provider_id falls back
   * to config.staffId and the name is derived from the provider record on read.
   */
  providerId?: string;
  volumeMl: number;
  milkType: MilkReceivableMilkType;
  status: MilkReceivableMilkStatus;
  /** Donor approval status (UI value). Required for DONOR source. */
  donorStatus: DonorApprovalStatus;
  location: string;
  /** storage_location_id of the selected Location, resolved by name from the storage-locations list */
  storageLocationId?: string;
  pumpedDate: string;
  expiryDate: string;
}

/**
 * Fetch paginated, filtered donor milk containers from the inventory management service.
 */
export async function fetchDonorMilkContainers(
  params: DonorMilkQueryParams,
): Promise<{ items: DonorMilkContainer[]; total: number }> {
  const data = await listMilkReceivables({
    source_type: 'DONOR' as const,
    milk_status: params.status?.length ? params.status.join(',') : undefined,
    milk_type: params.milkType?.length ? params.milkType.join(',') : undefined,
    donor_status: params.donorStatus?.length
      ? params.donorStatus.map((s) => DONOR_STATUS_TO_API[s as DonorApprovalStatus]).join(',')
      : undefined,
    search: params.search?.trim() || undefined,
    page: params.page ?? 1,
    limit: params.pageSize ?? 25,
  });

  return {
    items: data.items.map(toDonorMilkContainer),
    total: data.pagination.totalItems,
  };
}

/**
 * Fetch a single donor milk container by its receivable ID.
 * Called when opening the Edit/Delete modal to ensure the latest data is shown.
 */
export async function fetchDonorMilkContainerById(id: string): Promise<DonorMilkContainer> {
  const rec = await getMilkReceivableById(id, 'DONOR');
  return toDonorMilkContainer(rec);
}

/**
 * Delete a donor milk container by receivable ID (soft-delete / discard).
 */
export async function deleteDonorMilkContainer(id: string, reason?: string): Promise<boolean> {
  await discardMilkReceivable(id, {
    notes: reason?.trim() || 'Discarded via Donor Milk inventory screen',
  }, 'DONOR');
  return true;
}

export interface UpdateDonorMilkContainerPayload {
  volumeMl: number;
  milkType: MilkReceivableMilkType;
  status: MilkReceivableMilkStatus;
  location: string;
  storageLocationId?: string;
  pumpedDate: string;
  expiryDate: string;
  /** Donor approval status (UI value). Maps to backend donor_status. */
  donorStatus?: DonorApprovalStatus;
}

/**
 * Update an existing donor milk container.
 */
export async function updateDonorMilkContainer(
  id: string,
  payload: UpdateDonorMilkContainerPayload,
): Promise<DonorMilkContainer | null> {
  if (!config.hospitalId) {
    throw new Error('updateDonorMilkContainer: VITE_HOSPITAL_ID is not configured');
  }

  const rec = await updateMilkReceivable(id, {
    hospital_id: config.hospitalId,
    volume_ml: payload.volumeMl,
    milk_type: payload.milkType,
    milk_status: payload.status,
    location: payload.location,
    storage_location_id: payload.storageLocationId,
    received_at: payload.pumpedDate,
    expiry_at: payload.expiryDate || null,
    donor_status: payload.donorStatus
      ? DONOR_STATUS_TO_API[payload.donorStatus]
      : undefined,
  }, 'DONOR');
  return toDonorMilkContainer(rec);
}

/**
 * Create a new donor milk container (receivable).
 */
export async function createDonorMilkContainer(
  payload: CreateDonorMilkContainerPayload,
): Promise<DonorMilkContainer> {
  if (!config.hospitalId) {
    throw new Error('createDonorMilkContainer: VITE_HOSPITAL_ID is not configured');
  }
  if (!config.receivedBy) {
    throw new Error('createDonorMilkContainer: VITE_RECEIVED_BY (or VITE_STAFF_ID) is not configured');
  }
  const resolvedProviderId = payload.providerId ?? config.staffId;
  if (!resolvedProviderId) {
    throw new Error('createDonorMilkContainer: provider ID is unavailable and VITE_STAFF_ID is not configured');
  }

  const rec = await createMilkReceivable({
    hospital_id: config.hospitalId,
    receipt_number: payload.containerId,
    source_type: 'DONOR',
    // Use explicit providerId or fallback to configured staff ID
    provider_id: resolvedProviderId,
    received_by: config.receivedBy,
    volume_ml: payload.volumeMl,
    milk_type: payload.milkType,
    milk_status: payload.status,
    location: payload.location,
    storage_location_id: payload.storageLocationId ?? null,
    received_at: payload.pumpedDate,
    expiry_at: payload.expiryDate || null,
    donor_status: DONOR_STATUS_TO_API[payload.donorStatus],
  });
  return toDonorMilkContainer(rec);
}

/**
 * Create multiple donor milk containers in a single API call.
 * Sends all bottles in one POST /api/v1/milk-receivables request.
 */
export async function createDonorMilkContainersBulk(
  payloads: CreateDonorMilkContainerPayload[],
): Promise<{ containers: DonorMilkContainer[]; failed: BulkReceiveFailure[] }> {
  if (!config.hospitalId) {
    throw new Error('createDonorMilkContainersBulk: VITE_HOSPITAL_ID is not configured');
  }
  if (!config.receivedBy) {
    throw new Error('createDonorMilkContainersBulk: VITE_RECEIVED_BY (or VITE_STAFF_ID) is not configured');
  }

  const hospitalId = config.hospitalId;
  const receivedBy = config.receivedBy;

  const apiPayloads = payloads.map((payload) => {
    const resolvedProviderId = payload.providerId ?? config.staffId;
    if (!resolvedProviderId) {
      throw new Error('createDonorMilkContainersBulk: provider ID is unavailable and VITE_STAFF_ID is not configured');
    }
    return {
      hospital_id: hospitalId,
      receipt_number: payload.containerId,
      source_type: 'DONOR' as const,
      provider_id: resolvedProviderId,
      received_by: receivedBy,
      volume_ml: payload.volumeMl,
      milk_type: payload.milkType,
      milk_status: payload.status,
      location: payload.location,
      storage_location_id: payload.storageLocationId ?? null,
      received_at: payload.pumpedDate,
      expiry_at: payload.expiryDate || null,
      donor_status: DONOR_STATUS_TO_API[payload.donorStatus],
    };
  });

  const { created, failed } = await createMilkReceivablesBulk(apiPayloads);
  return {
    containers: created.map(toDonorMilkContainer),
    failed: failed.map((f) => ({ containerId: f.receipt_number, reason: f.reason })),
  };
}
